import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import JSZip from "jszip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/Dialog";
import { toast } from "../../hooks/use-toast";
import { Toaster } from "../ui/Toaster";
import { customFetch } from "../../utils/Auth";
import { environment } from "../../config/environment";
import { parseBstrFile, validateMacro } from "./utils/bstrParser";
import BoosterIssuesModal from "./BoosterIssuesModal";

export default function UploadMacrosPanel({ userId }) {
  const [parsedMacros, setParsedMacros] = useState([]);
  const [issuesMacros, setIssuesMacros] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showIssuesModal, setShowIssuesModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parseError, setParseError] = useState(null);

  const [macroEntries, setMacroEntries] = useState([
    { id: Date.now(), dotPhrase: "", keywords: "", content: "" },
  ]);
  const [isManualSubmitting, setIsManualSubmitting] = useState(false);
  const [manualSubmitStatus, setManualSubmitStatus] = useState({ type: "", message: "" });

  const [expandedAccordion, setExpandedAccordion] = useState(null);

  const [allDoctors, setAllDoctors] = useState([]);
  const [doctorSearch, setDoctorSearch] = useState("");
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isDoctorDropdownOpen, setIsDoctorDropdownOpen] = useState(false);
  const doctorDropdownRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const response = await customFetch(
          `${environment.apiUrl}/doctors/all-doctors`,
          { method: "GET" }
        );
        const data = await response.json();
        setAllDoctors(Array.isArray(data) ? data : []);
      } catch {
        setAllDoctors([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (doctorSearch.trim()) {
      const search = doctorSearch.trim().toLowerCase();
      setFilteredDoctors(
        allDoctors.filter((doc) => {
          const name = doc.user_name?.toLowerCase() || "";
          const id = doc.user_id?.toLowerCase() || "";
          const email = doc.user_email?.toLowerCase() || "";
          return name.includes(search) || id.includes(search) || email.includes(search);
        })
      );
      setIsDoctorDropdownOpen(true);
    } else {
      setFilteredDoctors([]);
      setIsDoctorDropdownOpen(false);
    }
  }, [doctorSearch, allDoctors]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (doctorDropdownRef.current && !doctorDropdownRef.current.contains(event.target)) {
        setIsDoctorDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleManualInputChange = (id, field, value) => {
    setMacroEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== id) return entry;
        if (field === "dotPhrase" && value && !value.startsWith(".")) {
          value = "." + value;
        }
        return { ...entry, [field]: value };
      })
    );
  };

  const addAnotherMacro = () => {
    setMacroEntries((prev) => [
      ...prev,
      { id: Date.now(), dotPhrase: "", keywords: "", content: "" },
    ]);
  };

  const removeMacro = (id) => {
    if (macroEntries.length > 1) {
      setMacroEntries((prev) => prev.filter((entry) => entry.id !== id));
    }
  };

  const isManualFormValid = () =>
    macroEntries.some(
      (entry) => entry.dotPhrase && entry.keywords && entry.content
    );

  const BATCH_LIMIT = 100;

  const batchUploadMacros = async (macros) => {
    if (!selectedDoctor) {
      toast({ title: "No user selected", description: "Please select a user before uploading macros.", variant: "destructive" });
      return { ok: false, summary: { created: 0, duplicate: 0, invalid: 0, error: 0, requested: 0 }, results: [] };
    }

    const allFormatted = macros.map((macro) => ({
      dot_phrase: macro.dot_phrase,
      keywords: macro.keywords
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k.length > 0),
      content: macro.content,
    }));

    const chunks = [];
    for (let i = 0; i < allFormatted.length; i += BATCH_LIMIT) {
      chunks.push(allFormatted.slice(i, i + BATCH_LIMIT));
    }

    const summary = { created: 0, duplicate: 0, invalid: 0, error: 0, requested: macros.length };
    const allResults = [];
    let httpOk = true;

    for (const chunk of chunks) {
      const response = await customFetch(
        `${environment.apiUrl}/macros/batch-upload`,
        {
          method: "POST",
          body: JSON.stringify({
            macros: chunk,
            doctor_id: selectedDoctor.user_id,
          }),
        }
      );

      if (response.ok) {
        const json = await response.json();
        const data = json.data || {};
        const chunkSummary = data.summary || {};
        summary.created += chunkSummary.created || 0;
        summary.duplicate += chunkSummary.duplicate || 0;
        summary.invalid += chunkSummary.invalid || 0;
        summary.error += chunkSummary.error || 0;
        if (Array.isArray(data.results)) {
          allResults.push(...data.results);
        }
      } else {
        httpOk = false;
      }
    }

    return { ok: httpOk, summary, results: allResults };
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!isManualFormValid()) return;
    setIsManualSubmitting(true);
    setManualSubmitStatus({ type: "", message: "" });

    const completeMacros = macroEntries.filter(
      (entry) => entry.dotPhrase && entry.keywords && entry.content
    );

    const macrosForUpload = completeMacros.map((macro) => ({
      dot_phrase: macro.dotPhrase,
      keywords: macro.keywords,
      content: macro.content,
    }));

    try {
      const result = await batchUploadMacros(macrosForUpload);
      const { summary } = result;

      if (result.ok && summary.created > 0) {
        const parts = [`Successfully uploaded ${summary.created} macro${summary.created !== 1 ? "s" : ""}!`];
        if (summary.duplicate > 0) {
          parts.push(`${summary.duplicate} duplicate${summary.duplicate !== 1 ? "s" : ""} skipped.`);
        }
        setManualSubmitStatus({ type: "success", message: parts.join(" ") });
        setMacroEntries([{ id: Date.now(), dotPhrase: "", keywords: "", content: "" }]);
      } else if (result.ok && summary.created === 0 && summary.duplicate > 0) {
        setManualSubmitStatus({
          type: "error",
          message: `All ${summary.duplicate} macro${summary.duplicate !== 1 ? "s" : ""} already exist. No new macros were added.`,
        });
      } else {
        setManualSubmitStatus({ type: "error", message: "Failed to upload macros. Please try again." });
      }
    } catch (error) {
      console.error("Error submitting macros:", error);
      setManualSubmitStatus({
        type: "error",
        message: "An error occurred while uploading macros. Please try again.",
      });
    } finally {
      setIsManualSubmitting(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setParseError(null);
    const allMacros = [];

    for (const file of acceptedFiles) {
      try {
        if (file.name.toLowerCase().endsWith(".zip")) {
          const zip = await JSZip.loadAsync(file);
          const bstrEntries = Object.keys(zip.files).filter(
            (name) => name.toLowerCase().endsWith(".bstr") && !zip.files[name].dir
          );

          if (bstrEntries.length === 0) {
            setParseError("No .bstr files found in the zip archive.");
            return;
          }

          for (const name of bstrEntries) {
            const content = await zip.files[name].async("string");
            allMacros.push(parseBstrFile(content, name));
          }
        } else if (file.name.toLowerCase().endsWith(".bstr")) {
          const content = await file.text();
          allMacros.push(parseBstrFile(content, file.name));
        }
      } catch (err) {
        setParseError(`Error processing ${file.name}: ${err.message}`);
        return;
      }
    }

    if (allMacros.length === 0) {
      setParseError("No valid .bstr files found.");
      return;
    }

    const valid = [];
    const withIssues = [];

    allMacros.forEach((macro) => {
      const issues = validateMacro(macro);
      if (issues.length > 0) {
        withIssues.push({ ...macro, issues });
      } else {
        valid.push(macro);
      }
    });

    if (valid.length > 0) {
      setParsedMacros(valid);
      setShowConfirmModal(true);
    }

    if (withIssues.length > 0) {
      setIssuesMacros(withIssues);
      if (valid.length === 0) {
        setShowIssuesModal(true);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/zip": [".zip"],
      "application/x-zip-compressed": [".zip"],
      "application/octet-stream": [".bstr"],
      "text/plain": [".bstr"],
    },
    multiple: true,
    noClick: isSubmitting,
    noDrag: isSubmitting,
  });

  const handleIssuesResolved = (fixedMacros) => {
    setIssuesMacros([]);
    setShowIssuesModal(false);
    setParsedMacros((prev) => [...prev, ...fixedMacros]);
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);

    try {
      const result = await batchUploadMacros(parsedMacros);
      const { summary, results: macroResults } = result;

      const failedMacros = (macroResults || []).filter(
        (r) => r.status === "error" || r.status === "invalid"
      );
      const duplicateMacros = (macroResults || []).filter(
        (r) => r.status === "duplicate"
      );

      if (!result.ok) {
        toast({ title: "Upload failed", description: "One or more batch requests failed.", variant: "destructive" });
      } else {
        const parts = [];
        if (summary.created > 0) {
          parts.push(`${summary.created}/${summary.requested} dot phrase${summary.created !== 1 ? "s" : ""} added`);
        }
        if (summary.duplicate > 0) {
          parts.push(`${summary.duplicate} duplicate${summary.duplicate !== 1 ? "s" : ""} skipped`);
        }
        if (failedMacros.length > 0) {
          parts.push(`${failedMacros.length} failed`);
        }

        const hasErrors = failedMacros.length > 0;
        const variant = hasErrors ? "destructive" : "success";
        const title = hasErrors ? "Import completed with errors" : "Import successful";

        if (parts.length > 0) {
          toast({ title, description: parts.join(", "), variant });
        } else {
          toast({ title: "No imports", description: "No dot phrases were imported.", variant: "destructive" });
        }
      }
    } catch {
      toast({ title: "Error", description: "An error occurred while uploading dot phrases.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
      setParsedMacros([]);
    }
  };

  const isUploadDisabled = !selectedDoctor;

  return (
    <div id="upload_macros_panel">
      <Toaster />
      <div className="flex flex-col gap-6">
        {/* User Selector */}
        <div className="flex flex-col gap-2">
          <label className="block text-sm font-medium text-gray-700">
            Select User <span className="text-red-500">*</span>
          </label>
          <div className="relative" ref={doctorDropdownRef}>
            {selectedDoctor ? (
              <div className="flex items-center justify-between px-4 py-3 bg-purple-50 border border-purple-200 rounded-md">
                <div>
                  <span className="text-sm font-medium text-gray-900">{selectedDoctor.user_name}</span>
                  <span className="text-sm text-gray-500 ml-2">({selectedDoctor.user_email})</span>
                  <span className="text-xs text-gray-400 ml-2">ID: {selectedDoctor.user_id}</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDoctor(null);
                    setDoctorSearch("");
                  }}
                  className="text-gray-400 hover:text-gray-600 ml-2"
                  aria-label="Clear selection"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={doctorSearch}
                  onChange={(e) => setDoctorSearch(e.target.value)}
                  onFocus={() => { if (doctorSearch.trim()) setIsDoctorDropdownOpen(true); }}
                  placeholder="Search by name, email, or user ID..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                {isDoctorDropdownOpen && filteredDoctors.length > 0 && (
                  <ul className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                    {filteredDoctors.map((doc) => (
                      <li key={doc.user_id}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedDoctor(doc);
                            setDoctorSearch("");
                            setIsDoctorDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-purple-50 text-sm"
                        >
                          <span className="font-medium text-gray-900">{doc.user_name}</span>
                          <span className="text-gray-500 ml-2">{doc.user_email}</span>
                          <span className="text-gray-400 text-xs ml-2">({doc.user_id})</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {isDoctorDropdownOpen && doctorSearch.trim() && filteredDoctors.length === 0 && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg px-4 py-3 text-sm text-gray-500">
                    No users found matching "{doctorSearch}"
                  </div>
                )}
              </>
            )}
          </div>
          {!selectedDoctor && (
            <p className="text-xs text-gray-500">You must select a user before uploading macros.</p>
          )}
        </div>

        {parseError && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
            <svg className="w-5 h-5 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span>{parseError}</span>
          </div>
        )}

        <div
          {...(isUploadDisabled ? {} : getRootProps())}
          className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors ${
            isUploadDisabled
              ? "opacity-50 cursor-not-allowed border-gray-200 bg-gray-50"
              : isSubmitting
              ? "opacity-60 pointer-events-none cursor-default border-gray-300"
              : isDragActive
              ? "border-purple-500 bg-purple-50 cursor-pointer"
              : "border-gray-300 hover:border-purple-400 cursor-pointer"
          }`}
        >
          {!isUploadDisabled && <input {...getInputProps()} />}
          {isSubmitting ? (
            <div className="flex flex-col items-center gap-4">
              <svg
                className="w-12 h-12 text-purple-500 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <p className="text-gray-700">Uploading dot phrases...</p>
              <p className="text-sm text-gray-500">Please wait, this may take a moment.</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {isDragActive ? (
                <p className="text-gray-700">Drop files here...</p>
              ) : (
                <>
                  <p className="text-gray-700">Drag & drop your Booster dot phrase folder or an individual file here</p>
                  <p className="text-sm text-gray-500">Accepts .zip archives or individual .bstr files — or click to browse</p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 pt-4">
          <button
            type="button"
            onClick={() => setShowManualModal(true)}
            disabled={isUploadDisabled}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add a dot phrase manually
          </button>
        </div>

        {/* Confirm Import Modal */}
        <Dialog
          open={showConfirmModal}
          onOpenChange={(isOpen) => {
            setShowConfirmModal(isOpen);
            if (!isOpen) setParsedMacros([]);
          }}
        >
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Confirm Import</DialogTitle>
              <DialogDescription>
                {parsedMacros.length} dot phrase{parsedMacros.length !== 1 ? "s" : ""} ready to import
                {selectedDoctor ? ` for ${selectedDoctor.user_name}` : ""}. Review and confirm.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-4 overflow-hidden">
              <div className="max-h-96 overflow-y-auto flex flex-col gap-2 pr-1">
                {parsedMacros.map((macro, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg">
                    <button
                      type="button"
                      onClick={() => setExpandedAccordion(expandedAccordion === index ? null : index)}
                      className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 rounded-lg"
                    >
                      <span>{macro.dot_phrase}</span>
                      <svg
                        className={`w-4 h-4 transition-transform ${expandedAccordion === index ? "rotate-180" : ""}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {expandedAccordion === index && (
                      <div className="px-4 pb-4 flex flex-col gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Keywords</label>
                          <input
                            type="text"
                            value={macro.keywords}
                            onChange={(e) => {
                              const updated = [...parsedMacros];
                              updated[index] = { ...updated[index], keywords: e.target.value };
                              setParsedMacros(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                          <textarea
                            value={macro.content}
                            onChange={(e) => {
                              const updated = [...parsedMacros];
                              updated[index] = { ...updated[index], content: e.target.value };
                              setParsedMacros(updated);
                            }}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-y"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setParsedMacros([]);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSubmit}
                  className="px-4 py-2 text-sm font-medium text-white bg-ktx-purple rounded-md hover:bg-purple-700"
                >
                  Submit {parsedMacros.length} Dot Phrase{parsedMacros.length !== 1 ? "s" : ""}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Issues Modal */}
        <BoosterIssuesModal
          open={showIssuesModal}
          onOpenChange={(isOpen) => {
            setShowIssuesModal(isOpen);
            if (!isOpen) setIssuesMacros([]);
          }}
          macros={issuesMacros}
          onResolved={handleIssuesResolved}
        />

        {/* Manual Entry Modal */}
        <Dialog open={showManualModal} onOpenChange={setShowManualModal}>
          <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Add Custom Dot Phrases</DialogTitle>
              <DialogDescription>
                Enter the details for the custom dot phrase you would like to add.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleManualSubmit} className="flex flex-col gap-4 overflow-hidden">
              {manualSubmitStatus.message && (
                <div
                  className={`flex items-start gap-2 p-3 rounded-md text-sm ${
                    manualSubmitStatus.type === "success"
                      ? "bg-green-50 border border-green-200 text-green-800"
                      : "bg-red-50 border border-red-200 text-red-800"
                  }`}
                >
                  <svg className="w-5 h-5 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {manualSubmitStatus.type === "success" ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    )}
                  </svg>
                  <span>{manualSubmitStatus.message}</span>
                </div>
              )}

              <div className="max-h-96 overflow-y-auto flex flex-col gap-4 pr-1">
                {macroEntries.map((entry, index) => (
                  <div key={entry.id} className="flex flex-col gap-3 p-4 border border-gray-200 rounded-lg">
                    {macroEntries.length > 1 && (
                      <div className="flex justify-between items-center">
                        <p className="text-sm font-semibold text-gray-800">Dot Phrase {index + 1}</p>
                        <button
                          type="button"
                          onClick={() => removeMacro(entry.id)}
                          className="text-sm text-red-600 hover:text-red-800"
                          aria-label={`Remove dot phrase ${index + 1}`}
                        >
                          Remove
                        </button>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dot Phrase <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={entry.dotPhrase}
                        onChange={(e) => handleManualInputChange(entry.id, "dotPhrase", e.target.value)}
                        placeholder="Enter dot phrase"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Keywords <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={entry.keywords}
                        onChange={(e) => handleManualInputChange(entry.id, "keywords", e.target.value)}
                        placeholder="Enter keywords (comma separated)"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">Tip: Our AI uses these keywords to suggest the most relevant dot phrases based on the audio.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Content <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={entry.content}
                        onChange={(e) => handleManualInputChange(entry.id, "content", e.target.value)}
                        placeholder="Enter dot phrase content"
                        rows={3}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-y"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addAnotherMacro}
                className="self-start text-sm font-medium text-ktx-purple hover:text-purple-700"
              >
                + Add Another Dot Phrase
              </button>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowManualModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isManualFormValid() || isManualSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-ktx-purple rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isManualSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
